import React, { JSX, useEffect, useMemo, useState } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { computed } from "mobx"
import { observer } from "mobx-react"
import { Button, Col, Flex, Row, Skeleton, Typography } from "antd"
import Form from "antd/es/form"
import {
  CopyOutlined,
  EditOutlined,
  EyeOutlined,
  PlusCircleOutlined,
  SaveOutlined,
} from "@ant-design/icons"

import { FormContainer } from "@src/app/components/FormContainer"
import { GlobalCurrenciesCollection } from "@src/app/CurrenciesCollection"
import { DefaultPageLayout } from "@src/app/layouts/DefaultPageLayout"
import { paths } from "@src/app/paths"
import { WidgetCallbackUrls } from "@src/pages/widgetModify/ui/WidgetCallbackUrls"
import { WidgetCode } from "@src/pages/widgetModify/ui/WidgetCode"
import { WidgetConfigFields } from "@src/pages/widgetModify/ui/WidgetConfigFields"
import { WidgetGeneralSettings } from "@src/pages/widgetModify/ui/WidgetGeneralSettings"
import { useCatchFormErrors } from "@src/shared/form/base_error"
import { paddingLimit } from "@src/shared/strings"
import { copy } from "@src/shared/utils/copy"
import css from "./index.module.css"
import { WidgetModifyStore } from "./store"

export const WidgetModifyPage = observer(() => {
  const navigate = useNavigate()
  const [getSearch] = useSearchParams()
  const { id } = useParams()
  const [form] = Form.useForm()
  const [formConfig] = Form.useForm()
  const [widgetModify] = useState(() => new WidgetModifyStore(navigate))
  const [isReadOnly, setIsReadOnly] = useState(!widgetModify.isNew)
  const [isCallbackUrlHidden, setIsCallbackUrlHidden] = useState(true)

  const [buttonText, buttonIcon, title] = computed<
    [string, JSX.Element, string]
  >(() => {
    return [
      widgetModify.isNew ? "Create" : "Save",
      widgetModify.isNew ? <PlusCircleOutlined /> : <SaveOutlined />,
      widgetModify.isNew ? "New Widget" : widgetModify.state.name,
    ] as [string, JSX.Element, string]
  }).get()

  const button = computed<JSX.Element>(() => {
    return isReadOnly ? (
      <Button
        htmlType="button"
        icon={<EditOutlined />}
        id="GENERATED_ID_WIDGETMODIFY_BUTTON_EDIT_1"
        loading={widgetModify.isLoading}
        onClick={() => setIsReadOnly(false)}
        size="large"
        type="primary"
      >
        Edit
      </Button>
    ) : (
      <Button
        htmlType="submit"
        icon={buttonIcon}
        id="GENERATED_ID_WIDGETMODIFY_BUTTON_VOID_1"
        loading={widgetModify.isLoading}
        onClick={() => submit(form.getFieldsValue())}
        size="large"
        type="primary"
      >
        {buttonText}
      </Button>
    )
  }).get()

  const { baseError, submit } = useCatchFormErrors(form, (values) => {
    const req = {
      ...values,
      ...(values.callback_url
        ? { callback_url: values.callback_url }
        : { callback_url: undefined }),
    }

    return widgetModify.modify(req)
  })

  useEffect(() => {
    if (id && id !== "new")
      widgetModify.init(id).then(({ currencies, state }) => {
        form.setFieldsValue(state)

        setIsReadOnly(!getSearch.get("editImmediately"))
      })
  }, [id])

  return (
    <DefaultPageLayout button={button} title={title} url={paths.widgets}>
      <Flex gap={32} vertical>
        <FormContainer
          baseError={baseError}
          className={css.formContainer}
          disabled={widgetModify.isLoading || isReadOnly}
          form={form}
          name="widget"
          onFinish={submit}
        >
          <div className={css.formWrapper}>
            <div className={css.left}>
              <WidgetGeneralSettings />
            </div>
            <div className={css.right}>
              <WidgetCallbackUrls />

              {isReadOnly && (
                <Flex vertical>
                  <div>Callback Signature Key</div>
                  <Row align="middle">
                    <Col span={18}>
                      {widgetModify.state.callback_signature_key && (
                        <Typography.Title level={5} style={{ margin: 0 }}>
                          {isCallbackUrlHidden
                            ? paddingLimit(
                                widgetModify.state.callback_signature_key,
                                0,
                                8,
                              )
                            : widgetModify.state.callback_signature_key}
                        </Typography.Title>
                      )}
                    </Col>
                    <Col span={6}>
                      <Flex gap={16} justify="end">
                        <Button
                          disabled={!widgetModify.state.callback_signature_key}
                          icon={<EyeOutlined />}
                          onClick={() => setIsCallbackUrlHidden((v) => !v)}
                          size="small"
                        />
                        <Button
                          disabled={!widgetModify.state.callback_signature_key}
                          icon={<CopyOutlined />}
                          onClick={() =>
                            copy(widgetModify.state.callback_signature_key!)
                          }
                          size="small"
                        />
                      </Flex>
                    </Col>
                  </Row>
                </Flex>
              )}
            </div>
          </div>
        </FormContainer>

        {widgetModify.state.code && isReadOnly && (
          <Form form={formConfig}>
            <Flex gap={32} vertical>
              <WidgetCode code={widgetModify.state.code} form={formConfig} />
              <WidgetConfigFields
                currencies={GlobalCurrenciesCollection()}
                form={formConfig}
              />
            </Flex>
          </Form>
        )}
      </Flex>
    </DefaultPageLayout>
  )
})
